import { environment } from '@env/environment';

/** Dev bypass for kiosk ordering-available — see `.cursor/skills/dood-webapp-local-debug/SKILL.md`. */
export const FORCE_ORDERING_QUERY_PARAM = 'forceOrdering';

/**
 * Explicit force-ordering flag from a query value.
 * `1` / `true` (case-insensitive) → on; `0` / `false` → off.
 * Missing or any other value → `null` (keep the current SPA state).
 */
export function parseForceOrderingQueryParam(raw: unknown): boolean | null {
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (value == null || value === '') {
    return null;
  }
  const normalized = String(value).trim().toLowerCase();
  if (normalized === '1' || normalized === 'true') {
    return true;
  }
  if (normalized === '0' || normalized === 'false') {
    return false;
  }
  return null;
}

export function resolveForceOrderingAvailable(
  queryValue: unknown,
  currentlyEnabled: boolean,
): boolean {
  const parsed = parseForceOrderingQueryParam(queryValue);
  return parsed === null ? currentlyEnabled : parsed;
}

export function readForceOrderingQueryFromUrl(url: string): string | null {
  const queryIndex = url.indexOf('?');
  if (queryIndex === -1) {
    return null;
  }
  const hashIndex = url.indexOf('#', queryIndex);
  const query = url.slice(queryIndex + 1, hashIndex === -1 ? undefined : hashIndex);
  return new URLSearchParams(query).get(FORCE_ORDERING_QUERY_PARAM);
}

/** Dev-only: localhost, loopback, or PR preview hosts. */
export function isForceOrderingHostAllowed(hostname: string): boolean {
  const host = hostname.trim().toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1') {
    return true;
  }
  return host.endsWith('.webapp-dev.dood.com');
}

/**
 * Whether `forceOrdering` may bypass GET ordering-available.
 * Requires non-production build and an allowed host.
 */
export function canUseForceOrderingBypass(hostname?: string): boolean {
  if (environment.production) {
    return false;
  }
  if (typeof window === 'undefined') {
    return false;
  }
  const host = hostname ?? window.location.hostname;
  return isForceOrderingHostAllowed(host);
}

export function resolveForceOrderingAvailableFromUrl(
  url: string,
  currentlyEnabled: boolean,
  hostname?: string,
): boolean {
  if (!canUseForceOrderingBypass(hostname)) {
    return false;
  }
  const queryValue = readForceOrderingQueryFromUrl(url);
  return resolveForceOrderingAvailable(queryValue, currentlyEnabled);
}
